/**
 * useGraphOverlay — Manages graph overlay state for analytics visualizations.
 *
 * When an analytics algorithm produces per-node scores (PageRank, Betweenness,
 * Community Detection), this hook applies visual overlays to the graph:
 * - Node size scaled by score
 * - Node color mapped to score range or community ID
 * - Legend data for the overlay
 */
import { useState, useCallback, useMemo } from 'react';

export type OverlayType = 'pagerank' | 'betweenness' | 'community' | 'risk' | null;

export interface OverlayNodeScore {
    nodeId: string;
    score: number;
    /** For community detection — cluster ID */
    clusterId?: number;
    label?: string;
}

export interface OverlayLegendItem {
    color: string;
    label: string;
    range?: string;  // e.g. "0.7–1.0"
}

export interface GraphOverlayState {
    /** Currently active overlay type */
    activeOverlay: OverlayType;
    /** Per-node scores for the active overlay */
    nodeScores: Map<string, OverlayNodeScore>;
    /** Legend items for the active overlay */
    legend: OverlayLegendItem[];
    /** Whether an overlay is currently being computed */
    isComputing: boolean;
}

// Color palettes
const SCORE_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444']; // green → red
const COMMUNITY_COLORS = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#06b6d4',
    '#84cc16', '#f43f5e', '#a855f7', '#14b8a6', '#e11d48'
]; // 10 distinct community colors

export function useGraphOverlay() {
    const [state, setState] = useState<GraphOverlayState>({
        activeOverlay: null,
        nodeScores: new Map(),
        legend: [],
        isComputing: false,
    });

    const setComputing = useCallback((computing: boolean) => {
        setState(prev => ({ ...prev, isComputing: computing }));
    }, []);

    const applyScoreOverlay = useCallback((
        type: 'pagerank' | 'betweenness' | 'risk',
        scores: OverlayNodeScore[]
    ) => {
        const nodeScores = new Map<string, OverlayNodeScore>();
        scores.forEach(s => nodeScores.set(s.nodeId, s));

        const legend: OverlayLegendItem[] = [
            { color: SCORE_COLORS[0], label: 'Low', range: '0.0–0.2' },
            { color: SCORE_COLORS[1], label: 'Below avg', range: '0.2–0.4' },
            { color: SCORE_COLORS[2], label: 'Average', range: '0.4–0.6' },
            { color: SCORE_COLORS[3], label: 'Above avg', range: '0.6–0.8' },
            { color: SCORE_COLORS[4], label: 'High', range: '0.8–1.0' },
        ];

        setState({
            activeOverlay: type,
            nodeScores,
            legend,
            isComputing: false,
        });
    }, []);

    const applyCommunityOverlay = useCallback((scores: OverlayNodeScore[]) => {
        const nodeScores = new Map<string, OverlayNodeScore>();
        const clusterIds = new Set<number>();
        scores.forEach(s => {
            nodeScores.set(s.nodeId, s);
            if (s.clusterId != null) clusterIds.add(s.clusterId);
        });

        const sortedClusters = Array.from(clusterIds).sort((a, b) => a - b);
        const legend: OverlayLegendItem[] = sortedClusters.map((cid, i) => ({
            color: COMMUNITY_COLORS[i % COMMUNITY_COLORS.length],
            label: `Community ${cid}`,
        }));

        setState({
            activeOverlay: 'community',
            nodeScores,
            legend,
            isComputing: false,
        });
    }, []);

    const clearOverlay = useCallback(() => {
        setState({
            activeOverlay: null,
            nodeScores: new Map(),
            legend: [],
            isComputing: false,
        });
    }, []);

    /** Get the overlay color for a node */
    const getNodeColor = useCallback((nodeId: string): string | null => {
        const score = state.nodeScores.get(nodeId);
        if (!score) return null;

        if (state.activeOverlay === 'community') {
            const idx = score.clusterId ?? 0;
            return COMMUNITY_COLORS[idx % COMMUNITY_COLORS.length];
        }

        // Score-based coloring
        const s = Math.min(1, Math.max(0, score.score));
        const idx = Math.min(SCORE_COLORS.length - 1, Math.floor(s * SCORE_COLORS.length));
        return SCORE_COLORS[idx];
    }, [state.nodeScores, state.activeOverlay]);

    /** Get the overlay size multiplier for a node (1.0–3.0) */
    const getNodeSize = useCallback((nodeId: string): number => {
        const score = state.nodeScores.get(nodeId);
        if (!score) return 1.0;
        if (state.activeOverlay === 'community') return 1.5; // uniform for communities
        return 1.0 + (score.score * 2.0); // 1x to 3x
    }, [state.nodeScores, state.activeOverlay]);

    return {
        ...state,
        setComputing,
        applyScoreOverlay,
        applyCommunityOverlay,
        clearOverlay,
        getNodeColor,
        getNodeSize,
    };
}
